import { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import { Facebook, Instagram, TrendingUp, TrendingDown, Users, Eye, MessageCircle, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Marketing = () => {
  const [socialData, setSocialData] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/analytics/social`);
        setSocialData(response.data);
      } catch (error) {
        console.error('Error fetching social data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getChangeIndicator = (current, previous) => {
    if (!previous || previous === 0) return { change: 'N/A', type: 'neutral' };
    const change = ((current - previous) / previous * 100).toFixed(0);
    return {
      change: change > 0 ? `+${change}%` : `${change}%`,
      type: change > 0 ? 'positive' : 'negative'
    };
  };

  if (loading || !socialData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      </Layout>
    );
  }

  const latestFb = socialData.comparison[3]?.facebook;
  const prevFb = socialData.comparison[2]?.facebook;
  const latestIg = socialData.comparison[3]?.instagram;
  const prevIg = socialData.comparison[2]?.instagram;

  const chartData = socialData.comparison.map(item => ({
    month: item.month.replace(' 2025', '').replace(' 2026', ''),
    'Facebook Reach': item.facebook.total_reach,
    'Instagram Reach': item.instagram.total_reach,
    'FB Followers': item.facebook.new_followers,
    'IG Followers': item.instagram.new_followers
  }));

  return (
    <Layout>
      <div className="space-y-6" data-testid="marketing-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Marketing Analytics</h1>
            <p className="text-slate-500 mt-1">Social media performance from GiriUK monthly reports</p>
          </div>
          <Badge variant="outline" className="text-purple-600 border-purple-200">Oct 2025 - Jan 2026</Badge>
        </div>

        {/* Platform Toggle */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="facebook">Facebook</TabsTrigger>
            <TabsTrigger value="instagram">Instagram</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Platform Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Facebook Card */}
              <Card className="bg-white shadow-sm overflow-hidden" data-testid="facebook-card">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Facebook className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Facebook</h3>
                      <p className="text-blue-100 text-sm">Jan 2026 Performance</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <Eye className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-slate-800">{latestFb?.total_reach?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Total Reach</p>
                      <Badge className={`mt-1 ${getChangeIndicator(latestFb?.total_reach, prevFb?.total_reach).type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {getChangeIndicator(latestFb?.total_reach, prevFb?.total_reach).change}
                      </Badge>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <Users className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-slate-800">{latestFb?.visits?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Page Visits</p>
                      <Badge className={`mt-1 ${getChangeIndicator(latestFb?.visits, prevFb?.visits).type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {getChangeIndicator(latestFb?.visits, prevFb?.visits).change}
                      </Badge>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <UserPlus className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-slate-800">{latestFb?.new_followers}</p>
                      <p className="text-xs text-slate-500">New Followers</p>
                      <Badge className={`mt-1 ${getChangeIndicator(latestFb?.new_followers, prevFb?.new_followers).type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {getChangeIndicator(latestFb?.new_followers, prevFb?.new_followers).change}
                      </Badge>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <MessageCircle className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-slate-800">{latestFb?.posts}</p>
                      <p className="text-xs text-slate-500">Posts</p>
                      <Badge className="mt-1 bg-slate-100 text-slate-600">Consistent</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Instagram Card */}
              <Card className="bg-white shadow-sm overflow-hidden" data-testid="instagram-card">
                <div className="bg-gradient-to-r from-pink-500 to-purple-500 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Instagram className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Instagram</h3>
                      <p className="text-pink-100 text-sm">Jan 2026 Performance</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <Eye className="h-5 w-5 text-pink-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-slate-800">{latestIg?.total_reach?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Total Reach</p>
                      <Badge className={`mt-1 ${getChangeIndicator(latestIg?.total_reach, prevIg?.total_reach).type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {getChangeIndicator(latestIg?.total_reach, prevIg?.total_reach).change}
                      </Badge>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <MessageCircle className="h-5 w-5 text-pink-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-slate-800">{latestIg?.content_interactions}</p>
                      <p className="text-xs text-slate-500">Interactions</p>
                      <Badge className={`mt-1 ${getChangeIndicator(latestIg?.content_interactions, prevIg?.content_interactions).type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {getChangeIndicator(latestIg?.content_interactions, prevIg?.content_interactions).change}
                      </Badge>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <Users className="h-5 w-5 text-pink-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-slate-800">{latestIg?.profile_visits}</p>
                      <p className="text-xs text-slate-500">Profile Visits</p>
                      <Badge className={`mt-1 ${getChangeIndicator(latestIg?.profile_visits, prevIg?.profile_visits).type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {getChangeIndicator(latestIg?.profile_visits, prevIg?.profile_visits).change}
                      </Badge>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <UserPlus className="h-5 w-5 text-pink-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-slate-800">{latestIg?.new_followers}</p>
                      <p className="text-xs text-slate-500">New Followers</p>
                      <Badge className={`mt-1 ${getChangeIndicator(latestIg?.new_followers, prevIg?.new_followers).type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {getChangeIndicator(latestIg?.new_followers, prevIg?.new_followers).change}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Trend Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white shadow-sm" data-testid="reach-trend-chart">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-800">Reach Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorFb" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorIg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#EC4899" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#EC4899" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                        <Legend />
                        <Area type="monotone" dataKey="Facebook Reach" stroke="#3B82F6" strokeWidth={2} fill="url(#colorFb)" />
                        <Area type="monotone" dataKey="Instagram Reach" stroke="#EC4899" strokeWidth={2} fill="url(#colorIg)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm" data-testid="followers-trend-chart">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-800">New Followers Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                        <Legend />
                        <Bar dataKey="FB Followers" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="IG Followers" fill="#EC4899" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="facebook">
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Facebook className="h-5 w-5 text-blue-500" />
                  Facebook Monthly Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Month</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">Total Reach</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">Page Visits</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">New Followers</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">Posts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socialData.comparison.map((item, index) => (
                        <tr key={item.month} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium text-slate-800">{item.month}</td>
                          <td className="p-3 text-right text-slate-600">{item.facebook.total_reach.toLocaleString()}</td>
                          <td className="p-3 text-right text-slate-600">{item.facebook.visits}</td>
                          <td className="p-3 text-right text-slate-600">{item.facebook.new_followers}</td>
                          <td className="p-3 text-right text-slate-600">{item.facebook.posts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="instagram">
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Instagram className="h-5 w-5 text-pink-500" />
                  Instagram Monthly Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Month</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">Total Reach</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">Interactions</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">Profile Visits</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">New Followers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socialData.comparison.map((item) => (
                        <tr key={item.month} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium text-slate-800">{item.month}</td>
                          <td className="p-3 text-right text-slate-600">{item.instagram.total_reach.toLocaleString()}</td>
                          <td className="p-3 text-right text-slate-600">{item.instagram.content_interactions}</td>
                          <td className="p-3 text-right text-slate-600">{item.instagram.profile_visits}</td>
                          <td className="p-3 text-right text-slate-600">{item.instagram.new_followers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Marketing;
